import { useMemo } from 'react';
import { useGetActivities, useGetAllTagsOfTagType } from 'api';
import { FEATURE_FLAGS, useFeatureFlag } from 'feature-flags';
import { useTranslations } from 'i18n';
import { ActivitiesSortEnum, Activity, Tags } from 'oas';
import { SelectMenuItem } from '@SBGSports/catapult-core/dist/components/select/__commons__/selectCommons';
import { alphabeticalSort, FETCH_ACTIVITIES_PAGE_SIZE, getEndOfDay, getStartOfDay } from 'shared';
import { ACTIVITY_TAG_TYPE, DATA_SHARING_TAG_TYPE, DAY_CODE_TAG_TYPE } from 'shared/constants';
import {
    TagGroupKey,
    stripTagGroupSuffix,
    TAG_GROUP_KEY_ACTIVITY,
    TAG_GROUP_KEY_DATA_SHARING,
    TAG_GROUP_KEY_DAY_CODE,
} from 'shared/utils/tag-filter-keys';
import { useSideMenuFilterState } from 'shared/activity-side-menu/use-side-menu-filter-state';

export type GroupedTagItem = SelectMenuItem & { group: string };

export interface TagOption {
    id: string;
    value: string;
    isSelected?: boolean;
    isDisabled?: boolean;
}

const isMDTag = (name: string) => /^MD(?:[+-]\d+|\d*)$/i.test(name);

const mdNumericValue = (name: string) => parseInt(name.replace(/MD/i, '') || '0', 10);

const getDateForQuery = (date: Date | undefined, shouldNullDate: boolean) => {
    return shouldNullDate || !date ? undefined : date.getTime() / 1000;
};

// Sort day code tags: MD tags numerically first, then non-MD alphabetically
const dayCodeSort = (a: string, b: string) => {
    const aMD = isMDTag(a);
    const bMD = isMDTag(b);
    if (aMD && bMD) {
        return mdNumericValue(a) - mdNumericValue(b);
    }
    if (aMD) {
        return -1;
    }
    if (bMD) {
        return 1;
    }
    return a.localeCompare(b);
};

const buildGroupItems = (
    tags: Tags[] | undefined,
    tagNamesOnActivities: Set<string>,
    urlTagNames: Set<string>,
    groupLabel: string,
    sortFn: (a: string, b: string) => number,
): GroupedTagItem[] => {
    if (!tags) {
        return [];
    }
    const byName = new Map(tags.map((t) => [t.name ?? '', t]));
    const visibleNames = tags
        .filter((t) => t.name && (tagNamesOnActivities.has(t.name) || urlTagNames.has(t.name)))
        .map((t) => t.name ?? '')
        .sort(sortFn);

    return visibleNames.map((name) => ({
        id: byName.get(name)?.id ?? name,
        value: name,
        isSelected: false,
        group: groupLabel,
    }));
};

export const useFetchTags = (tagFilters: string[] = []) => {
    const { __ } = useTranslations();
    const releaseDataSharing = useFeatureFlag(FEATURE_FLAGS.RELEASE_DATASHARING);
    const { data: dayCodeTags, isLoading: dayCodeLoading } = useGetAllTagsOfTagType(DAY_CODE_TAG_TYPE);
    const { data: activityTags, isLoading: activityTagsLoading } = useGetAllTagsOfTagType(ACTIVITY_TAG_TYPE);
    const { data: dataSharingTags, isLoading: dataSharingTagsLoading } = useGetAllTagsOfTagType(
        releaseDataSharing ? DATA_SHARING_TAG_TYPE : '',
    );
    const { textFilter, dateFilters } = useSideMenuFilterState();
    const dayCodeGroupLabel = __('misc.day_code');
    const activityGroupLabel = __('misc.activity');
    const dataSharingGroupLabel = __('datasharing.data_sharing_tag_group');

    // Fetch activities without tag filter to determine which tags are present
    const activitiesList = useGetActivities(
        FETCH_ACTIVITIES_PAGE_SIZE,
        ActivitiesSortEnum.StartTimeDesc,
        0,
        undefined,
        getDateForQuery(getStartOfDay(dateFilters.from), false),
        getDateForQuery(getEndOfDay(dateFilters.to), false),
        textFilter,
        {
            getNextPageParam: (lastPage: Activity[], pages: Activity[][]) =>
                lastPage.length >= FETCH_ACTIVITIES_PAGE_SIZE ? pages.length + 1 : undefined,
        },
        undefined,
    );

    const { items, groupOrder, groupKeyMap, keyToGroupLabel } = useMemo(() => {
        if (!activitiesList.data) {
            return {
                items: [] as GroupedTagItem[],
                groupOrder: [] as string[],
                groupKeyMap: {} as Record<string, TagGroupKey>,
                keyToGroupLabel: {} as Partial<Record<TagGroupKey, string>>,
            };
        }

        const allActivities = activitiesList.data.pages.flatMap((page) => page);

        // Collect all tag names present on activities
        const tagNamesOnActivities = new Set<string>();
        allActivities.forEach((activity) => {
            activity.tags?.forEach((tag) => {
                if (tag.name) {
                    tagNamesOnActivities.add(tag.name);
                }
            });
        });

        // Extract tag names from filter keys — strip "~groupKey" suffix for disambiguated tags
        const urlTagNames = new Set(tagFilters.map(stripTagGroupSuffix));

        // Tag group definitions
        // TODO: remove release flag condition on data sharing once fully released
        const tagGroups: {
            label: string;
            groupKey: TagGroupKey;
            tags: Tags[] | undefined;
            sortFn: (a: string, b: string) => number;
        }[] = [
            { label: dayCodeGroupLabel, groupKey: TAG_GROUP_KEY_DAY_CODE, tags: dayCodeTags, sortFn: dayCodeSort },
            {
                label: activityGroupLabel,
                groupKey: TAG_GROUP_KEY_ACTIVITY,
                tags: activityTags,
                sortFn: alphabeticalSort,
            },
            ...(releaseDataSharing
                ? [
                      {
                          label: dataSharingGroupLabel,
                          groupKey: TAG_GROUP_KEY_DATA_SHARING,
                          tags: dataSharingTags,
                          sortFn: alphabeticalSort,
                      },
                  ]
                : []),
        ];

        const result: GroupedTagItem[] = [];
        const groups: string[] = [];
        const keyMap: Record<string, TagGroupKey> = {};
        const labelMap: Partial<Record<TagGroupKey, string>> = {};

        tagGroups.forEach(({ label, groupKey, tags, sortFn }) => {
            const groupItems = buildGroupItems(tags, tagNamesOnActivities, urlTagNames, label, sortFn);
            result.push(...groupItems);
            if (groupItems.length > 0) {
                groups.push(label);
                keyMap[label] = groupKey;
                labelMap[groupKey] = label;
            }
        });

        return { items: result, groupOrder: groups, groupKeyMap: keyMap, keyToGroupLabel: labelMap };
    }, [
        dayCodeTags,
        activityTags,
        dataSharingTags,
        releaseDataSharing,
        activitiesList.data,
        tagFilters,
        dayCodeGroupLabel,
        activityGroupLabel,
        dataSharingGroupLabel,
    ]);

    return {
        items,
        groupOrder,
        groupKeyMap,
        keyToGroupLabel,
        isLoading:
            dayCodeLoading ||
            activityTagsLoading ||
            (releaseDataSharing && dataSharingTagsLoading) ||
            activitiesList.isLoading,
    };
};
